// @flow

// This is needed for indexing the sessions and is not actually used for anything
// Currently there are only one type of sessions.
import { useEffect, useRef } from "react";

export const DEFAULT_SESSION_TYPE = 0;

// This is needed for indexing the messages and is not actually used for anything
// Currently there are only one type of sessions.
export const DEFAULT_MESSAGE_TYPE = 0;

export const PUBLISH_LINE_BREAK = "^ENTER^";

export function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback && savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useImperativeDisableScroll(element: any, disabled: boolean) {
  useEffect(() => {
    if (!element) {
      return;
    }

    element.style.overflowY = disabled ? "hidden" : "scroll";

    return () => {
      element.style.overflowY = "scroll";
    };
  }, [disabled]);
}

// 브라우저마다 IME 조합 중 keydown을 다르게 보고한다.
// Chrome/Safari는 isComposing, 일부 환경은 keyCode 229로만 알린다.
export function isComposingEvent(nativeEvent: any): boolean {
  return nativeEvent?.isComposing === true || nativeEvent?.keyCode === 229;
}

// 한글 입력창에서 Enter로 확정(submit)할 때 쓰는 훅.
//
// 조합 중 눌린 Enter는 IME가 마지막 글자를 확정하는 키다. 그 시점에 submit하면
// 확정된 글자가 비워진 입력창에 뒤늦게 들어가, 한 글자짜리 중복 입력이 만들어진다.
// (Input 애드립 중복 전송 #146, 북마크 중복 생성 #150)
//
// 그래서 조합 중 Enter는 플래그만 세워두고, 조합이 끝난 뒤 확정된 값으로 한 번만
// 실행한다. Enter를 두 번 눌러야 하는 불편 없이 기존 UX가 유지된다.
//
// onSubmit은 React state 대신 입력창의 DOM 값을 받는다. 조합 중에는 state가
// 한 틱 늦기 때문이다. 반환된 ref를 입력창에 반드시 연결해야 한다.
export function useImeSafeEnter(
  onSubmit: (value: string) => void,
  externalRef?: any
): any {
  const internalRef = useRef(null);
  const inputRef = externalRef ?? internalRef;
  const pendingRef = useRef(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const submit = () => {
    pendingRef.current = false;
    onSubmitRef.current(inputRef.current ? inputRef.current.value : "");
  };

  return {
    ref: inputRef,
    // "이 이벤트를 소비했는지"를 반환한다. Enter가 아니면 false이므로,
    // 호출부에서 Escape 등 다른 키를 이어서 처리할 수 있다.
    handleKeyDown: (event: any): boolean => {
      if (event.key !== "Enter") {
        return false;
      }
      if (isComposingEvent(event.nativeEvent)) {
        pendingRef.current = true;
        return true;
      }
      event.preventDefault();
      submit();
      return true;
    },
    handleCompositionStart: () => {
      // 이전 조합에서 남은 플래그가 엉뚱한 시점에 실행되지 않도록 초기화
      pendingRef.current = false;
    },
    handleCompositionEnd: () => {
      if (!pendingRef.current) {
        return;
      }
      // compositionend 직후 input 이벤트로 값이 한 번 더 갱신되므로,
      // 타이머로 한 틱 미뤄 확정된 최종 값을 읽는다.
      setTimeout(() => {
        // 조합 확정 후 Enter keydown을 한 번 더 보내는 환경에서는 그쪽에서
        // 이미 실행됐으므로 여기서는 아무것도 하지 않는다.
        if (!pendingRef.current || !inputRef.current) {
          return;
        }
        submit();
      }, 0);
    },
    // 버튼 클릭 등 Enter 외의 경로로 확정할 때
    submitNow: submit,
    // Escape 등으로 입력을 취소할 때
    cancel: () => {
      pendingRef.current = false;
    },
  };
}

export const defaultKeyCommands = [
  {
    commandName: "clearSubtitles",
    triggerKey: "F8",
  },
  {
    commandName: "focusInput",
    triggerKey: "F1",
  },
  {
    commandName: "moveDown",
    triggerKey: "ArrowDown",
  },
  {
    commandName: "moveUp",
    triggerKey: "ArrowUp",
  },
  {
    commandName: "sendScript",
    triggerKey: "Off",
  },
  {
    commandName: "sendTranslation",
    triggerKey: "F4",
  },
];

export const functionKeys = [
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
  "ArrowUp",
  "ArrowDown",
  "Off",
];

export const normalizeOrgList = (
  org: ?(Array<string> | string)
): Array<string> => {
  if (!org) return [];

  if (Array.isArray(org)) {
    return org
      .flatMap((x) => (x ?? "").split(",")) // 혹시 배열 원소가 "A,B"일 수도
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return org
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

export const normalizeSessionOrg = (orgName: ?string): Array<string> => {
  if (!orgName) return [];
  return orgName
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};
